import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Form } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import CustomerUser from '../../../models/CustomerUser';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import FormValidator from '../../../services/FormValidator';
import { allowEmpty } from '../../../util/formValidators';
import CheckboxField from '../../form/CheckboxField';
import FormGroupField from '../../form/FormGroupField';

const labels = {
  firstname: <FormattedMessage {...globalTranslations.firstNameTitle} />,
  lastname: <FormattedMessage {...globalTranslations.lastNameTitle} />,
  email: <FormattedMessage {...globalTranslations.emailTitle} />,
  telephone: <FormattedMessage {...globalTranslations.telephoneTitle} />,
  loginName: <FormattedMessage {...globalTranslations.emailOrUsernameTitle} />,
  password: <FormattedMessage {...globalTranslations.passwordTitle} />,
  isActive: <FormattedMessage {...globalTranslations.isActiveLabel} />,
  notifyUser: <FormattedMessage {...globalTranslations.notifyUserLabel} />,
  allowUserCreateAddress: (
    <FormattedMessage {...globalTranslations.allowUserCreateAddressLabel} />
  ),
  allowUserMakePurchase: (
    <FormattedMessage {...globalTranslations.allowUserMakePurchaseLabel} />
  ),
};

const createOption = (value, label) => {
  return {
    value,
    label,
  };
};

@observer
export class UserForm extends Component {
  constructor(props) {
    super(props);
    const { formValidator, user } = this.props;

    this.form = null;
    this.formName = 'UserForm';

    this.form = createForm({
      firstname: createFormField(
        { value: user && user.firstname },
        formValidator.firstName(labels.firstname)
      ),
      lastname: createFormField(
        { value: user && user.lastname },
        formValidator.lastName(labels.firstname)
      ),
      email: createFormField(
        { value: user && user.email },
        formValidator.email(labels.email)
      ),
      telephone: createFormField(
        { value: user && user.telephone },
        formValidator.telephone(labels.telephone).map(allowEmpty)
      ),
      loginName: createFormField(
        { value: user && user.login_name },
        formValidator.username(labels.loginName)
      ),
      password: createFormField(
        {},
        user ? null : formValidator.password(labels.password)
      ),
      allowUserMakePurchase: createFormField({
        value: user && user.allow_make_purchases === false ? '' : '1',
      }),
      // Apparently our form handles string zero, '0', as a valid value and we have to use empty string.
      allowUserCreateAddress: createFormField({
        value: user && user.allow_address_modifications === false ? '' : '1',
      }),
      isActive: createFormField({
        value: user && user.is_active === false ? '' : '1',
      }),
      notifyUser: createFormField({
        value: '1',
      }),
    });
  }

  handleSubmit = (e) => {
    const { onSubmit } = this.props;

    e.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      onSubmit(this.form.getFieldValues());
    }
  };

  getField = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);

    return (
      field.active && (
        <FormGroupField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <CheckboxField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  render() {
    const { loading, buttonText, user } = this.props;

    return (
      <Form className={this.formName} noValidate onSubmit={this.handleSubmit}>
        {this.getField('firstname', {
          autoComplete: 'given-name',
          required: true,
        })}
        {this.getField('lastname', {
          autoComplete: 'family-name',
          required: true,
        })}
        {this.getField('email', {
          type: 'email',
          autoComplete: 'email',
          required: true,
        })}
        {this.getField('telephone', {
          type: 'tel',
          autoComplete: 'tel-national',
        })}
        {this.getField('loginName', {
          autocapitalization: 'off',
          autoComplete: 'username',
          required: true,
        })}
        {this.getField('password', {
          type: 'password',
          autoComplete: 'new-password',
          required: true,
          disabled: !!user,
        })}
        {this.checkbox('allowUserMakePurchase', {
          options: createOption('1', labels.allowUserMakePurchase),
        })}
        {this.checkbox('allowUserCreateAddress', {
          options: createOption('1', labels.allowUserCreateAddress),
        })}
        {this.checkbox('isActive', {
          options: createOption('1', labels.isActive),
          disabled: !!!user,
        })}
        {this.checkbox('notifyUser', {
          options: createOption('1', labels.notifyUser),
          disabled: !!!user,
        })}
        <Button color="primary" disabled={this.form.valid === false || loading}>
          {buttonText}
        </Button>
      </Form>
    );
  }
}

UserForm.propTypes = {
  loading: PropTypes.bool,
  buttonText: PropTypes.node.isRequired,
  user: modelOf(CustomerUser),
  onSubmit: PropTypes.func.isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired,
};

export default inject('formValidator')(UserForm);
